import { Controller } from "@hotwired/stimulus"
import { useDebounce } from "stimulus-use"

// Remembers which field the user moved to before an autosave so we can restore
// focus after the round-trip re-renders the form (see onSubmit/restoreFocus).
const REFOCUS_STORAGE_KEY = "autosaveInputRefocusName"

export default class extends Controller {
  static debounces = ['onSubmit']
  static values = {
    triggerEvent: String
  }

  initialize() {
    useDebounce(this)
    this.initialValue = this.element.value
    this.targetElement = this.element

    if (this.element.inputElement) {
      // is rich text element / <trix-editor>
      this.targetElement = this.element.inputElement
    }
    this.element.addEventListener(this.triggerEventValue, this.onTriggerEvent.bind(this))
    this.targetElement.form.addEventListener("turbo:submit-end", this.onSubmitEnd.bind(this))

    if (this.element.dataset.autosaveInputIncludeOtherFormTarget) {
      this.includeOtherFormTarget = this.element.dataset.autosaveInputIncludeOtherFormTarget
    }
  }

  connect() {
    this.restoreFocus()
  }

  // After an autosave re-renders the page, return focus (and the cursor) to the
  // field the user had moved to, matched by its form field name (which is
  // identical across the new/edit variants of a form).
  restoreFocus() {
    const name = window.sessionStorage.getItem(REFOCUS_STORAGE_KEY)
    if (!name || this.element.name !== name) return

    window.sessionStorage.removeItem(REFOCUS_STORAGE_KEY)
    this.element.focus()

    // Place the cursor at the end so continued typing appends. Some input
    // types (email, number) don't support selection ranges and throw.
    const length = this.element.value ? this.element.value.length : 0
    try {
      this.element.setSelectionRange(length, length)
    } catch (e) {
      // ignore unsupported input types
    }
  }

  onTriggerEvent(e) {
    if (this.element.type === 'radio' || this.element.type === 'checkbox') {
      // For radio buttons and checkboxes the value attribute is static, so
      // submit whenever the toggle event fires.
      this.onSubmit(e)
    } else if (this.initialValue != this.element.value) {
      this.onSubmit(e)
    }
  }

  // Helper function to copy values from one form element to another
  copyInputValues(srcElement, destForm) {
    const newInput = document.createElement('input');
    newInput.type = 'hidden';
    newInput.name = srcElement.name;
    newInput.value = srcElement.value;
    destForm.appendChild(newInput);
  }

  onSubmit(e) {
    // This runs after the debounce, by which point focus has already moved to
    // the field the user tabbed/clicked into. Remember it so we can restore
    // focus there once the autosave re-renders the form.
    const active = document.activeElement
    if (active && active.name) {
      try {
        window.sessionStorage.setItem(REFOCUS_STORAGE_KEY, active.name)
      } catch (e) {
        // sessionStorage may be unavailable (e.g. Safari private mode); focus
        // restoration is best-effort, so don't let it block the autosave.
      }
    }

    if (this.includeOtherFormTarget) {
      const otherForm = document.querySelector(`[data-autosave-input-form-target='${this.includeOtherFormTarget}']`);

      // If otherForm exists, we'll get all inputs, select, and textarea elements and copy their values to the target form
      if (otherForm) {
        const currentForm = this.targetElement.form;
        const otherFormElements = otherForm.querySelectorAll('input, select, textarea');

        otherFormElements.forEach((element) => {
          switch (element.tagName.toLowerCase()) {
            case 'input':
            case 'textarea':
              this.copyInputValues(element, currentForm);
              break;
            case 'select':
              if (element.selectedOptions.length > 0) {
                const newInput = document.createElement('input');
                newInput.type = 'hidden';
                newInput.name = element.name;
                newInput.value = element.selectedOptions[0].value;
                currentForm.appendChild(newInput);
              }
              break;
          }
        });
      }
    }

    this.targetElement.form.requestSubmit()
  }

  onSubmitEnd(e) {
    // do something on end
  }

}